// Tanya ETAMS — tombol + panel asisten AI staf (K200 butir 9–15, 27 Sep 2026).
//
// Jawaban hanya dari Panduan yang disahkan Owner, menyebut sumbernya, dan menautkan layar yang bisa dibuka peran
// penanya. Asisten ini hanya MEMBACA; semua perubahan tetap lewat layar biasa. Tampil "belum aktif" sampai Owner
// memasang kunci penyedia AI.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircleQuestion, Send, BookOpen, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { useAuth } from "../../contexts/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import Hint from "./Hint";

// Cadangan bila status belum membawa contoh (peran tak dikenal / backend lama).
const CONTOH_CADANGAN = [
  "Di mana saya mengajukan izin atau cuti?",
  "Saya lupa absen masuk tadi pagi, bagaimana?",
  "Tanya ETAMS bisa bantu apa saja?",
  "Aturan apa saja yang ada di Panduan untuk saya?",
];

function Balon({ item, onTutup }) {
  if (item.dari === "saya") {
    return (
      <div className="ml-8 rounded-lg bg-info-soft px-3 py-2 text-sm text-info-soft-foreground" data-testid="tanya-pertanyaan">
        {item.teks}
      </div>
    );
  }
  const tidakTahu = (item.hasil && item.hasil !== "DIJAWAB") || Boolean(item.penolakan);
  return (
    <div className={`mr-4 rounded-lg border px-3 py-2 text-sm ${tidakTahu ? "border-warning-soft-border bg-warning-soft text-warning-soft-foreground" : "bg-card"}`}
      data-testid="tanya-jawaban">
      {item.tanyaBalik && (
        <p className="mb-1 text-xs font-medium text-muted-foreground" data-testid="tanya-balik">
          Supaya jawabannya tepat, saya perlu tahu dulu:
        </p>
      )}
      <p className="whitespace-pre-line leading-relaxed">{item.teks}</p>
      {item.sumber?.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-1 text-xs text-muted-foreground" data-testid="tanya-sumber">
          <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
          Sumber:
          {item.sumber.map((s) => (
            <Link key={s.kode} to={`/panduan#${s.kode}`} onClick={onTutup} className="underline underline-offset-2">
              Panduan › {s.judul}
            </Link>
          ))}
        </p>
      )}
      {/* VONIS-27SEP-TANYA-RAMAH butir 3: jalan menu + tombol Buka. Backend hanya mengirim layar/rincian yang
          boleh dibuka peran penanya. */}
      {item.tautan?.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {item.tautan.map((t) => (
            <li key={t.path} className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background px-2 py-1.5"
              data-testid="tanya-tautan">
              <span className="text-xs text-muted-foreground" data-testid="tanya-jalan-menu">
                {t.jalan_menu || t.judul}
              </span>
              <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                <Link to={t.path} onClick={onTutup} data-testid="tanya-buka" aria-label={`Buka ${t.judul}`}>
                  Buka <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TanyaEtams() {
  const { isOwner } = useAuth();
  const lokasi = useLocation();
  const [buka, setBuka] = useState(false);
  const [status, setStatus] = useState(null);
  const [teks, setTeks] = useState("");
  const [percakapan, setPercakapan] = useState([]);
  const [sibuk, setSibuk] = useState(false);
  const bawah = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!buka) return;
    api.get("/tanya-etams/status")
      .then((r) => setStatus(r.data))
      .catch((e) => setStatus({ aktif: false, pesan: apiErrorMessage(e,
        "Tanya ETAMS belum bisa dibuka sekarang. Coba lagi sebentar lagi, atau buka menu Panduan.") }));
  }, [buka]);

  useEffect(() => { bawah.current?.scrollIntoView({ block: "end" }); }, [percakapan, sibuk]);

  const kirim = useCallback(async (e) => {
    e?.preventDefault();
    const q = teks.trim();
    if (q.length < 2 || sibuk) return;
    setTeks("");
    setPercakapan((p) => [...p, { dari: "saya", teks: q }]);
    setSibuk(true);
    try {
      const r = await api.post("/tanya-etams", { pertanyaan: q, layar: lokasi.pathname });
      // Saldo layanan AI habis: pesan istirahat ikut tampil di atas, sampai satu pertanyaan berhasil lagi.
      if (r.data.penolakan === "SALDO_HABIS") {
        setStatus((s) => ({ ...(s || {}), aktif: true, saldo_habis: true, pesan: r.data.jawaban }));
      } else if (r.data.hasil !== "GAGAL") {
        setStatus((s) => (s?.saldo_habis ? { ...s, saldo_habis: false, pesan: null } : s));
      }
      setPercakapan((p) => [...p, { dari: "ai", teks: r.data.jawaban, hasil: r.data.hasil,
        sumber: r.data.sumber, tautan: r.data.tautan, tanyaBalik: r.data.tanya_balik,
        penolakan: r.data.penolakan }]);
    } catch (err) {
      setPercakapan((p) => [...p, { dari: "ai", hasil: "GAGAL",
        teks: apiErrorMessage(err,
          "Tanya ETAMS sedang tidak bisa menjawab karena ada gangguan sesaat. Coba lagi beberapa menit lagi, atau buka menu Panduan.") }]);
    } finally {
      setSibuk(false);
    }
  }, [teks, sibuk, lokasi.pathname]);

  const aktif = status?.aktif;
  // Saldo layanan AI habis (TANYA-ETAMS-SALDO-GEMINI-HABIS-01): pesan istirahat tampil sebelum staf mengetik. Kolom
  // input tetap terbuka — begitu saldo diisi ulang, pertanyaan berikutnya langsung terjawab.
  const saldoHabis = Boolean(aktif && status?.saldo_habis);
  return (
    <Sheet open={buka} onOpenChange={setBuka}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1 px-2" data-testid="tombol-tanya-etams" aria-label="Tanya ETAMS">
          <MessageCircleQuestion className="h-4 w-4" aria-hidden="true" />
          <span className="hidden md:inline">Tanya ETAMS</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-3 p-4 sm:max-w-md" data-testid="panel-tanya-etams">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-1">
            Tanya ETAMS
            <Hint>Jawaban diambil dari Panduan yang sudah disahkan Owner. Tanya ETAMS hanya membaca, tidak bisa
              mengubah data.</Hint>
          </SheetTitle>
        </SheetHeader>

        {status && !aktif && (
          <p className="rounded-md border border-warning-soft-border bg-warning-soft p-3 text-sm text-warning-soft-foreground"
            data-testid="tanya-belum-aktif">
            {status.pesan || "Tanya ETAMS belum aktif."}
          </p>
        )}
        {saldoHabis && (
          <p className="rounded-md border border-warning-soft-border bg-warning-soft p-3 text-sm text-warning-soft-foreground"
            data-testid="tanya-saldo-habis">
            {status.pesan || "Tanya ETAMS sedang istirahat karena saldo layanan AI-nya habis. Untuk sekarang, buka menu Panduan."}
          </p>
        )}
        {isOwner && status?.pemakaian && (
          <p className="text-xs text-muted-foreground" data-testid="tanya-pemakaian">
            Pemakaian bulan ini Rp{Math.round(status.pemakaian.biaya_rp).toLocaleString("id-ID")} dari
            Rp{status.pemakaian.batas_rp.toLocaleString("id-ID")} ·{" "}
            <Link to="/tanya-etams/rekap" onClick={() => setBuka(false)} className="underline">lihat rekap</Link>
          </p>
        )}

        <div className="flex-1 space-y-3 overflow-y-auto pr-1">
          {/* VONIS-27SEP-TANYA-RAMAH butir 7: contoh pertanyaan sesuai peran (dari backend); ketuk untuk mengisi. */}
          {percakapan.length === 0 && aktif && !saldoHabis && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Contoh pertanyaan untuk Anda (ketuk untuk memakai):</p>
              <div className="flex flex-col gap-1.5">
                {(status?.contoh?.length ? status.contoh : CONTOH_CADANGAN).map((c) => (
                  <button key={c} type="button" data-testid="tanya-contoh"
                    onClick={() => { setTeks(c); inputRef.current?.focus(); }}
                    className="rounded-md border bg-card px-3 py-2 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {percakapan.map((it, i) => <Balon key={i} item={it} onTutup={() => setBuka(false)} />)}
          {sibuk && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sedang mencari jawaban…
            </p>
          )}
          <div ref={bawah} />
        </div>

        <form onSubmit={kirim} className="flex items-end gap-2">
          <Textarea ref={inputRef} value={teks} onChange={(e) => setTeks(e.target.value)} disabled={!aktif || sibuk}
            placeholder={aktif ? "Tulis pertanyaan Anda…" : "Belum bisa dipakai"} rows={2} maxLength={2000}
            className="min-h-[44px] resize-none" data-testid="tanya-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) kirim(e); }} />
          <Button type="submit" disabled={!aktif || sibuk || teks.trim().length < 2} aria-label="Kirim pertanyaan"
            data-testid="tanya-kirim">
            <Send className="h-4 w-4" aria-hidden="true" />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
